import React, { useState, useMemo } from 'react';
import { TrendingUp, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { useTopStudents } from '../../hooks/useTopStudents';
import { useCompanySports } from '../../hooks/useCompanySports';
import { StudentDetailModal } from './StudentDetailModal';
import { TopStudent } from '../../hooks/useTopStudents';

export function TopStudentsSection() {
  const [timePeriod, setTimePeriod] = useState<'week' | 'month' | 'quarter' | 'year'>('week');
  const [selectedStudent, setSelectedStudent] = useState<TopStudent | null>(null);
  
  // Fetch all students (no sport filter) to get cumulative ranking and group by sport
  const { data: allStudents, isLoading } = useTopStudents(null, timePeriod, 50);
  const { data: sports } = useCompanySports();

  const handleStudentClick = (student: TopStudent) => {
    setSelectedStudent(student);
  };

  const handleViewFullProfile = () => {
    if (selectedStudent) {
      window.location.href = `/students?student=${selectedStudent.student_id}`;
    }
  };

  // Calculate cumulative ranking and group by sport
  const { cumulativeStudents, studentsBySport } = useMemo(() => {
    if (!allStudents || !sports) {
      return { cumulativeStudents: [], studentsBySport: new Map() };
    }
    
    // Group by student_id and sum attendance across all sports for cumulative
    const studentMap = new Map<string, {
      student_id: string;
      first_name: string | null;
      last_name: string | null;
      email: string | null;
      avatar_url: string | null;
      totalAttendance: number;
      sports: Set<string>;
    }>();

    // Group by sport_id for sport columns
    const sportMap = new Map<string, TopStudent[]>();

    allStudents.forEach(student => {
      // Cumulative ranking
      const existing = studentMap.get(student.student_id);
      if (existing) {
        existing.totalAttendance += student.attendance_count;
        existing.sports.add(student.sport_name);
      } else {
        studentMap.set(student.student_id, {
          student_id: student.student_id,
          first_name: student.first_name,
          last_name: student.last_name,
          email: student.email,
          avatar_url: student.avatar_url,
          totalAttendance: student.attendance_count,
          sports: new Set([student.sport_name]),
        });
      }

      // Group by sport
      if (!sportMap.has(student.sport_id)) {
        sportMap.set(student.sport_id, []);
      }
      const sportStudents = sportMap.get(student.sport_id);
      if (sportStudents) {
        sportStudents.push(student);
      }
    });

    // Sort students within each sport by rank
    sportMap.forEach((students, _sportId) => {
      students.sort((a, b) => a.rank - b.rank);
    });

    // Calculate cumulative ranking
    const cumulative = Array.from(studentMap.values())
      .sort((a, b) => b.totalAttendance - a.totalAttendance)
      .slice(0, 10)
      .map((student, index) => ({
        ...student,
        rank: index + 1,
        sportsList: Array.from(student.sports),
      }));

    return { cumulativeStudents: cumulative, studentsBySport: sportMap };
  }, [allStudents, sports]);

  return (
    <>
      <Card className="bg-[#221F1F] border-[#504A4A] min-w-0 max-w-full overflow-hidden">
        <CardHeader>
          <CardTitle className="text-text-primary flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-rollops-accent" />
            Top Students
          </CardTitle>
        </CardHeader>
        <CardContent className="min-w-0 max-w-full">
          {/* Period Filter */}
          <div className="flex items-center gap-2 mb-6">
            <label className="text-sm text-text-secondary">Period:</label>
            <select
              value={timePeriod}
              onChange={(e) => setTimePeriod(e.target.value as 'week' | 'month' | 'quarter' | 'year')}
              className="bg-[#363232] border border-[#504A4A] text-text-primary rounded px-3 py-1.5 text-sm"
            >
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
              <option value="year">Year</option>
            </select>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421]" />
            </div>
          ) : (
            <div className="overflow-x-auto min-w-0 max-w-full">
              {/* Scrollable container - columns scroll horizontally when they exceed container width */}
              <div className="inline-flex gap-4">
                {/* Cumulative Ranking Column (Left) */}
                <div className="flex-shrink-0 w-64">
                  <div className="sticky left-0 bg-[#221F1F] pb-2 mb-2 border-b border-[#504A4A]">
                    <h3 className="text-sm font-semibold text-text-primary mb-3">Overall Ranking</h3>
                  </div>
                  <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                    {cumulativeStudents.length > 0 ? (
                      cumulativeStudents.map((student) => {
                        const studentName = `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Unknown';
                        // Find the full student object for click handler
                        const fullStudent = allStudents?.find(s => s.student_id === student.student_id);
                        return (
                          <button
                            key={student.student_id}
                            onClick={() => fullStudent && handleStudentClick(fullStudent)}
                            className="w-full flex items-center gap-3 p-2 rounded-lg bg-[#363232] hover:bg-[#504A4A] transition-colors text-left"
                          >
                            <div className="flex-shrink-0">
                              {student.avatar_url ? (
                                <img
                                  src={student.avatar_url}
                                  alt={studentName}
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-[#9B1421] flex items-center justify-center">
                                  <User className="w-4 h-4 text-white" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-text-primary">
                                  #{student.rank}
                                </span>
                                <span className="text-xs font-semibold text-text-primary truncate">
                                  {studentName}
                                </span>
                              </div>
                              <div className="text-xs text-text-secondary mt-0.5">
                                {student.totalAttendance} classes
                              </div>
                              {student.sportsList.length > 0 && (
                                <div className="text-xs text-text-tertiary mt-0.5">
                                  {student.sportsList.join(', ')}
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })
                    ) : (
                      <div className="text-center py-8 text-text-tertiary text-xs">
                        No data
                      </div>
                    )}
                  </div>
                </div>

                {/* Sport Columns */}
                {sports?.map((sport) => {
                  const sportStudents = (studentsBySport.get(sport.id) || []).slice(0, 5);
                  return (
                    <div key={sport.id} className="flex-shrink-0 w-64">
                      <div className="sticky left-0 bg-[#221F1F] pb-2 mb-2 border-b border-[#504A4A]">
                        <h3 className="text-sm font-semibold text-text-primary mb-3">{sport.sport_name}</h3>
                      </div>
                      <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                        {sportStudents.length > 0 ? (
                          sportStudents.map((student: TopStudent) => {
                            const studentName = `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Unknown';
                            return (
                              <button
                                key={`${student.student_id}-${student.sport_id}`}
                                onClick={() => handleStudentClick(student)}
                                className="w-full flex items-center gap-3 p-2 rounded-lg bg-[#363232] hover:bg-[#504A4A] transition-colors text-left"
                              >
                                <div className="flex-shrink-0">
                                  {student.avatar_url ? (
                                    <img
                                      src={student.avatar_url}
                                      alt={studentName}
                                      className="w-8 h-8 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-8 h-8 rounded-full bg-[#9B1421] flex items-center justify-center">
                                      <User className="w-4 h-4 text-white" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-text-primary">
                                      #{student.rank}
                                    </span>
                                    <span className="text-xs font-semibold text-text-primary truncate">
                                      {studentName}
                                    </span>
                                  </div>
                                  <div className="text-xs text-text-secondary mt-0.5">
                                    {student.attendance_count} {student.attendance_count === 1 ? 'class' : 'classes'}
                                  </div>
                                </div>
                              </button>
                            );
                          })
                        ) : (
                          <div className="text-center py-8 text-text-tertiary text-xs">
                            No data
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Student Detail Modal */}
      {selectedStudent && (
        <StudentDetailModal
          student={selectedStudent}
          onClose={() => setSelectedStudent(null)}
          onViewFullProfile={handleViewFullProfile}
        />
      )}
    </>
  );
}
